/**
 * TableWrapper Component with Copy Functionality
 *
 * Wraps tables with a copy-to-clipboard button that appears on hover
 * Copies table data in a tab-separated format suitable for pasting into spreadsheets
 */

import React, { useState } from 'react';

interface TableWrapperProps {
  children: React.ReactNode;
}

export const TableWrapper: React.FC<TableWrapperProps> = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // Extract table data from the DOM
      const table = document.querySelector('.table-copy-target');
      if (!table) return;

      // Extract text content from table cells
      const rows: string[] = [];
      const tableRows = table.querySelectorAll('tr');

      tableRows.forEach((row) => {
        const cells: string[] = [];
        row.querySelectorAll('th, td').forEach((cell) => {
          // Get text content and clean up whitespace
          const text = cell.textContent?.trim() || '';
          cells.push(text);
        });
        if (cells.length > 0) {
          // Join cells with tabs for spreadsheet compatibility
          rows.push(cells.join('\t'));
        }
      });

      const tableText = rows.join('\n');

      await navigator.clipboard.writeText(tableText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy table:', err);
    }
  };

  return (
    <div className="relative group my-4">
      {/* Copy button - appears on hover */}
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 px-3 py-1.5 bg-slate-700/80 hover:bg-slate-600/80 text-slate-300 hover:text-white rounded text-xs font-medium transition-all opacity-0 group-hover:opacity-100 flex items-center space-x-1.5 z-10"
        aria-label="Copy table to clipboard"
      >
        {copied ? (
          <>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>Copied!</span>
          </>
        ) : (
          <>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy Table</span>
          </>
        )}
      </button>

      {/* Table content */}
      <div className="overflow-x-auto">
        <div className="table-copy-target">{children}</div>
      </div>
    </div>
  );
};
